import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { jobPostingApi, JobPosting, CreateJobPostingData, UpdateJobPostingData, JobPostingFilters } from '../../services/jobPostingApi';

interface JobPostingState {
    jobPostings: JobPosting[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    } | null;
    filters: JobPostingFilters;
    loading: boolean;
    error: string | null;
    selectedJobPosting: JobPosting | null;
}

const initialState: JobPostingState = {
    jobPostings: [],
    pagination: null,
    filters: {
        search: '',
        status: '',
        page: 1,
        per_page: 20
    },
    loading: false,
    error: null,
    selectedJobPosting: null
};

// Async thunks
export const fetchJobPostings = createAsyncThunk(
    'jobPostings/fetchJobPostings',
    async (filters: JobPostingFilters = {}) => {
        const response = await jobPostingApi.getJobPostings(filters);
        return response;
    }
);

export const fetchJobPosting = createAsyncThunk(
    'jobPostings/fetchJobPosting',
    async (id: number) => {
        const response = await jobPostingApi.getJobPosting(id);
        return response;
    }
);

export const createJobPosting = createAsyncThunk(
    'jobPostings/createJobPosting',
    async (data: CreateJobPostingData) => {
        const response = await jobPostingApi.createJobPosting(data);
        return response;
    }
);

export const updateJobPosting = createAsyncThunk(
    'jobPostings/updateJobPosting',
    async ({ id, data }: { id: number; data: UpdateJobPostingData }) => {
        const response = await jobPostingApi.updateJobPosting(id, data);
        return response;
    }
);

export const deleteJobPosting = createAsyncThunk(
    'jobPostings/deleteJobPosting',
    async (id: number) => {
        await jobPostingApi.deleteJobPosting(id);
        return id;
    }
);

export const toggleJobPostingStatus = createAsyncThunk(
    'jobPostings/toggleJobPostingStatus',
    async (id: number) => {
        const response = await jobPostingApi.toggleJobPostingStatus(id);
        return response;
    }
);

const jobPostingSlice = createSlice({
    name: 'jobPostings',
    initialState,
    reducers: {
        setFilters: (state, action: PayloadAction<Partial<JobPostingFilters>>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters: (state) => {
            state.filters = {
                search: '',
                status: '',
                page: 1,
                per_page: 20
            };
        },
        setSelectedJobPosting: (state, action: PayloadAction<JobPosting | null>) => {
            state.selectedJobPosting = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        // Fetch job postings
        builder
            .addCase(fetchJobPostings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchJobPostings.fulfilled, (state, action) => {
                state.loading = false;
                state.jobPostings = action.payload.job_postings;
                state.pagination = action.payload.pagination;
            })
            .addCase(fetchJobPostings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch job postings';
            });

        // Fetch single job posting
        builder
            .addCase(fetchJobPosting.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchJobPosting.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedJobPosting = action.payload;
            })
            .addCase(fetchJobPosting.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch job posting';
            });

        // Create job posting
        builder
            .addCase(createJobPosting.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createJobPosting.fulfilled, (state, action) => {
                state.loading = false;
                state.jobPostings.unshift(action.payload);
                if (state.pagination) {
                    state.pagination.total += 1;
                }
            })
            .addCase(createJobPosting.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to create job posting';
            });

        // Update job posting
        builder
            .addCase(updateJobPosting.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateJobPosting.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.jobPostings.findIndex(job => job.id === action.payload.id);
                if (index !== -1) {
                    state.jobPostings[index] = action.payload;
                }
                if (state.selectedJobPosting?.id === action.payload.id) {
                    state.selectedJobPosting = action.payload;
                }
            })
            .addCase(updateJobPosting.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to update job posting';
            });

        // Delete job posting
        builder
            .addCase(deleteJobPosting.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteJobPosting.fulfilled, (state, action) => {
                state.loading = false;
                state.jobPostings = state.jobPostings.filter(job => job.id !== action.payload);
                if (state.pagination) {
                    state.pagination.total -= 1;
                }
            })
            .addCase(deleteJobPosting.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to delete job posting';
            });

        // Toggle job posting status
        builder
            .addCase(toggleJobPostingStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(toggleJobPostingStatus.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.jobPostings.findIndex(job => job.id === action.payload.id);
                if (index !== -1) {
                    state.jobPostings[index] = action.payload;
                }
                if (state.selectedJobPosting?.id === action.payload.id) {
                    state.selectedJobPosting = action.payload;
                }
            })
            .addCase(toggleJobPostingStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to toggle job posting status';
            });
    }
});

export const { setFilters, clearFilters, setSelectedJobPosting, clearError } = jobPostingSlice.actions;
export default jobPostingSlice.reducer;
